import { useCallback, useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Id, Version } from '@contracts/common';
import type {
  ContactBody, PartnerCreateBody, PartnerDetailDTO, PartnerKind, PartnerListDTO, PartnerPatchBody,
} from '@contracts/partners';
import { firstLetter } from '@/lib/initials';
import { api } from '../../api/client';
import { usePreservedMutation } from '../../api/query';

/**
 * The partner directory's reads and writes (projects & stakeholders spec §3.3, §5.2). Every write answers with the
 * partner as it now stands, so the detail is written straight into its cache and the directory is re-read.
 */

export type PartnerFilter = { q?: string; label?: string; kind?: PartnerKind | '' };

/** `/api/partners?q=…&label=…&kind=…`, empty parts left out, in that order. */
export function partnersPath({ q = '', label = '', kind = '' }: PartnerFilter): string {
  const parts: string[] = [];
  if (q.trim() !== '') parts.push(`q=${encodeURIComponent(q.trim())}`);
  if (label !== '') parts.push(`label=${encodeURIComponent(label)}`);
  if (kind !== '') parts.push(`kind=${encodeURIComponent(kind)}`);
  return parts.length === 0 ? '/api/partners' : `/api/partners?${parts.join('&')}`;
}

export const PARTNERS_KEY = ['partners'] as const;
export const partnerKey = (id: Id) => ['partner', id] as const;

export function usePartners(filter: PartnerFilter, enabled = true) {
  const path = partnersPath(filter);
  return useQuery({
    queryKey: [...PARTNERS_KEY, path],
    queryFn: () => api<PartnerListDTO>(path),
    enabled,
    // A new search keeps the rows on screen until its answer arrives, so the table never flashes empty.
    placeholderData: keepPreviousData,
  });
}

export function usePartner(id: Id, enabled = true) {
  return useQuery({
    queryKey: partnerKey(id), queryFn: () => api<PartnerDetailDTO>(`/api/partners/${id}`), enabled: enabled && id !== '',
  });
}

/** Writes the answer into the detail's cache and has the directory re-read. */
function useStore() {
  const queryClient = useQueryClient();
  return useCallback((detail: PartnerDetailDTO | null, gone?: Id) => {
    if (detail !== null) queryClient.setQueryData(partnerKey(detail.id), detail);
    // Marked stale, not re-read now: the page still showing it is about to leave, and the next visit asks again.
    if (gone !== undefined) void queryClient.invalidateQueries({ queryKey: partnerKey(gone), refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
  }, [queryClient]);
}

export function useCreatePartner() {
  const store = useStore();
  return usePreservedMutation(useCallback(async (body: Required<PartnerCreateBody>, requestId: Id) => {
    const detail = await api<PartnerDetailDTO>('/api/partners', { method: 'POST', body, requestId });
    store(detail);
    return detail;
  }, [store]));
}

export function useUpdatePartner(id: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (body: PartnerPatchBody, requestId: Id) => {
    const detail = await api<PartnerDetailDTO>(`/api/partners/${id}`, { method: 'PATCH', body, requestId });
    store(detail);
    return detail;
  }, [id, store]));
}

export function useDeletePartner(id: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (version: Version, requestId: Id) => {
    const answer = await api<{ id: Id }>(`/api/partners/${id}`, { method: 'DELETE', body: { version }, requestId });
    store(null, id);
    return answer;
  }, [id, store]));
}

export function useMergePartner(id: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (
    { targetId, version, targetVersion }: { targetId: Id; version: Version; targetVersion: Version }, requestId: Id,
  ) => {
    const detail = await api<PartnerDetailDTO>(`/api/partners/${id}/merge-into/${targetId}`, {
      method: 'POST', body: { version, targetVersion }, requestId,
    });
    store(detail, id);
    return detail;
  }, [id, store]));
}

export type ContactInput = Required<ContactBody>;

export function useSaveContact(partnerId: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (
    { contactId, version, body }: { contactId: Id | null; version: Version; body: ContactInput }, requestId: Id,
  ) => {
    const detail = contactId === null
      ? await api<PartnerDetailDTO>(`/api/partners/${partnerId}/contacts`, { method: 'POST', body, requestId })
      : await api<PartnerDetailDTO>(`/api/partners/${partnerId}/contacts/${contactId}`, {
        method: 'PATCH', body: { version, ...body }, requestId,
      });
    store(detail);
    return detail;
  }, [partnerId, store]));
}

export function useDeleteContact(partnerId: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (
    { contactId, version }: { contactId: Id; version: Version }, requestId: Id,
  ) => {
    const detail = await api<PartnerDetailDTO>(`/api/partners/${partnerId}/contacts/${contactId}`, {
      method: 'DELETE', body: { version }, requestId,
    });
    store(detail);
    return detail;
  }, [partnerId, store]));
}

/** An empty field is sent as `null`, never as `''`. */
export const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/**
 * A person's initials are the first and last word's ("Bùi Tâm" → "BT"); an organisation's the last two words'
 * ("Công ty CP Thép Minh Long" → "ML"), where its own name sits after the legal form.
 */
export function initials(name: string, organisation = false): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return '?';
  const pick = words.length === 1 ? [words[0]!] : organisation ? words.slice(-2) : [words[0]!, words[words.length - 1]!];
  return pick.map(firstLetter).join('').toUpperCase();
}

/** `value`, once it has stopped changing for `delay` ms. */
export function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}
